import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { ANIMATION_SPEED, BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles } from '@/styles';
import { animationsComplete, isSafari } from '@/utils';

/**
 * @element fg-dialog
 */
@customElement('fg-dialog')
@localized()
export class FgDialog extends LitElement {
  private dialogRef: Ref<HTMLDialogElement> = createRef();

  @property({ type: Boolean })
  fullscreen = false;

  @property({ type: Boolean })
  compact = false;

  onDialogKeyDown = (event: KeyboardEvent) => {
    if (event.key == 'Escape') {
      this.close();
    }
  };

  async firstUpdated() {
    const dialog = this.dialogRef.value;

    if (dialog !== undefined) {
      await animationsComplete(dialog);
      dialog.removeAttribute('loading');
    }
  }

  public open() {
    const dialog = this.dialogRef.value;
    window.addEventListener('keydown', this.onDialogKeyDown);

    if (dialog === undefined) {
      return;
    }

    dialog.removeAttribute('inert');
    dialog.showModal();
  }

  public close() {
    window.removeEventListener('keydown', this.onDialogKeyDown);
    this.dialogRef.value?.setAttribute('inert', '');
    this.dialogRef.value?.close('close');
    this.dispatchEvent(new Event('dialog-closed'));
  }

  private clickOutsideDismiss(event: Event) {
    const dialog = this.dialogRef.value;

    if (dialog !== undefined && event.target === dialog) {
      this.close();
    }
  }

  render() {
    const dialogRefTemplate = ref(this.dialogRef),
      dialogCssClasses = {
        fullscreen: this.fullscreen,
        'max-content': isSafari(),
        compact: this.compact,
      };

    return html`
      <dialog
        ${dialogRefTemplate}
        inert
        loading
        @click=${this.clickOutsideDismiss}
        class=${classMap(dialogCssClasses)}
      >
        <form method="dialog">
          <header>
            <h3><slot name="title"></slot></h3>
            <button
              @click=${this.close}
              type="button"
              .title=${t('actions.closeDialog') || ''}
            >
              <svg
                width="17"
                height="16"
                viewBox="0 0 17 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.5312 12.9688C16.1406 13.5312 16.1406 14.5156 15.5312 15.0781C15.25 15.3594 14.875 15.5 14.5 15.5C14.0781 15.5 13.7031 15.3594 13.4219 15.0781L8.5 10.1562L3.53125 15.0781C3.25 15.3594 2.875 15.5 2.5 15.5C2.07812 15.5 1.70312 15.3594 1.42188 15.0781C0.8125 14.5156 0.8125 13.5312 1.42188 12.9688L6.34375 8L1.42188 3.07812C0.8125 2.51562 0.8125 1.53125 1.42188 0.96875C1.98438 0.359375 2.96875 0.359375 3.53125 0.96875L8.5 5.89062L13.4219 0.96875C13.9844 0.359375 14.9688 0.359375 15.5312 0.96875C16.1406 1.53125 16.1406 2.51562 15.5312 3.07812L10.6094 8.04688L15.5312 12.9688Z"
                  fill="black"
                />
              </svg>
            </button>
            <span><slot name="pre-body"></slot></span>
          </header>
          <article>
            <slot></slot>
          </article>
          ${!this.fullscreen
            ? html`
                <footer>
                  <slot name="footer"></slot>
                </footer>
              `
            : nothing}
        </form>
      </dialog>
    `;
  }

  static styles = [
    baseStyles,
    css`
      dialog {
        display: grid;
        background: white;
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        border: 0;
        padding: 0;
        margin: auto;
        margin-block-end: 0;
        position: fixed;
        inset: 0;
        width: 100vw;
        max-width: 100vw;
        overflow: hidden;
        box-shadow: var(--fg-boxshadow--xl, var(--fg-fallback-boxshadow--xl));
        transition: opacity ${ANIMATION_SPEED}s;
      }

      dialog[loading] {
        visibility: hidden;
      }

      dialog[open] {
        animation: slide-in-up ${ANIMATION_SPEED}s cubic-bezier(0, 0, 0.3, 1);
      }

      dialog:not([open]) {
        display: grid;
        pointer-events: none;
        opacity: 0;
        animation: slide-out-down ${ANIMATION_SPEED}s ease-in;
      }

      dialog::backdrop {
        background: rgba(0, 0, 0, 0.5);
      }

      dialog + .backdrop {
        /* polyfill */
        background: rgba(0, 0, 0, 0.5);
        height: 100%;
        left: 0;
        position: fixed;
        top: 0;
        width: 100%;
      }

      dialog.max-content {
        height: auto;
        max-height: max-content;
      }

      dialog form {
        display: grid;
        grid-template-rows: auto 1fr auto;
        align-items: start;
        max-block-size: 90vh;
        padding: 20px 15px 30px;
      }

      header {
        display: flex;
        justify-content: space-between;
        padding-bottom: 25px;
        position: relative;
      }

      header h3 {
        margin: 0;
        font-size: 22px;
      }

      :host([part='wholesale']) header h3 {
        font-family: var(--fg-font-family--brand);
        font-weight: 600;
        color: var(--fg-primary);
      }

      :host([part='wholesale']) header button {
        color: var(--fg-secondary);
      }

      header button {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        flex-shrink: 0;
      }

      header span {
        position: absolute;
        bottom: 5px;
        left: 0;
      }

      article {
        overflow-y: auto;
        max-block-size: 100%; /* safari */
        overscroll-behavior-y: contain;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        dialog {
          border-radius: var(
            --fg-borderradius--xl,
            var(--fg-fallback-borderradius--xl)
          );
          margin-block-end: auto;
          width: auto;
          max-inline-size: min(90vw, 600px);
        }

        :host([part='wholesale']) dialog {
          max-inline-size: min(90vw, 760px);
        }

        :host([part='wholesale']) dialog.compact {
          max-inline-size: min(90vw, 540px);
        }

        :host([part='wholesale']) dialog.compact header {
          padding-bottom: 12px;
        }

        dialog[open] {
          animation: slide-in-up ${ANIMATION_SPEED}s
            cubic-bezier(0.5, -0.1, 0.1, 1.2);
        }

        dialog form {
          padding: 30px 40px 50px;
        }

        :host([part='wholesale']) dialog form {
          padding: 32px;
        }

        header {
          padding-bottom: 33px;
        }

        header h3 {
          font-size: 28px;
        }

        article {
          padding-top: 12px;
        }

        dialog.fullscreen {
          border-radius: 0;
          height: 100vh;
          max-height: 100vh;
          max-inline-size: 100vw;
          margin: 0;
          transition: none;
          animation: none;
        }

        dialog.fullscreen form {
          max-block-size: 100vh;
          padding: 20px;
        }

        dialog.fullscreen header {
          padding: 0;
        }

        dialog.fullscreen article {
          height: 100%;
        }
      }

      @keyframes slide-in-up {
        from {
          transform: translateY(100%);
        }
      }

      @keyframes slide-out-down {
        to {
          transform: translateY(100%);
        }
      }
    `,
  ];
}
